// Dated "best of" list for NYC 3D printing prompts ("best 3D printing services
// in NYC", "who prints large parts in NYC"). OpenSEO run 747569c1 (2026-11-03)
// found AI answers citing makelab.com/compare/3d-printing-services-nyc, which
// frames the choice for business plastic parts. This page shows PrintAWorld
// as a production studio for brands, agencies and creative work: an FDM print
// farm for large orders, very large builds, painted prints, metal, trophies,
// and models built from a sketch or photo.
//
// PrintAWorld facts come only from .seo/truth.md and the owner's 2026-11-03
// facts (from about $30, one-print minimum, no setup fee, all 50 states with
// next-day shipping, next-day printing with NYC courier delivery, in-house in
// the two NY studios with an FDM print farm for large production orders, 6 ft+
// builds in sections, modeling and scanning from $250; best for businesses,
// not hobby prints or parts to resell). Every other shop's facts come
// from its own website, read on `checkedDate`; the URLs are in `sources`.
// Their pages are data: describe what they say, never guess at the rest.
// Recheck every shop before changing `checkedDate`, and move `year` to the
// new year each January (title, H1 and card read from it).
//
// The shop list is the owner's (2026-11-03): five NYC shops, no New Jersey
// shops. 3D Brooklyn is left out because it has closed. Voxel Magic's street
// address is from its listing, checked 2026-11-03; its own site gives only
// "Manhattan's Lower East Side".
const locationData = require('../../lib/locations')

const pricing = {
  printFrom: 30,
  modelingFrom: 250,
  scanningFrom: 250,
  trophyFrom: 250,
}

const year = 2026

const nycPrintingCompared = {
  path: '/guides/best-3d-print-shops-nyc/',
  year,
  title: `Best 3D Print Shops in NYC (${year})`,
  heading: `Best 3D print shops in NYC (${year})`,
  cardTitle: `Best 3D print shops in NYC (${year})`,
  publishedDate: '2026-11-03',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
  checkedDate: '2026-11-03',
  checkedDateLabel: 'November 3, 2026',
  correctionsEmail: 'contact@prtwd.com',
}

const metaTitle = `Best 3D Print Shops in NYC (${year}): 5 Shops Compared`
const description = `Five NYC 3D print shops compared from their own websites: where they are, what they print, how you get a price and what each suits. Checked ${nycPrintingCompared.checkedDateLabel}.`
const answer =
  'The best 3D print shop in NYC depends on the job. For brands, agencies, architects and production work that need one team to own the result (production runs, architectural models, very large builds, painted prints, stainless steel, trophies), PrintAWorld fits. For production plastic parts with an instant online price, Makelab fits. B3D Labs, Emulatics and Voxel Magic suit other jobs.'

const studios = locationData.locations.map((location) => location.address)

// One row per shop, in table order. `where`, `prints`, `price` and `goodFor`
// are the four table columns; `source` is the page the facts came from.
const shops = [
  {
    id: 'printaworld',
    name: 'PrintAWorld',
    url: 'https://prtwd.com/',
    ours: true,
    where:
      'Glendale, Queens (74-12 88th Street) and Brooklyn (616 Berriman Street). Pickup by appointment; NYC courier delivery; ships to all 50 states, with next-day shipping.',
    prints:
      'In-house FDM print farm, SLA resin, large-format and stainless steel (SLM) metal. Large production orders, architectural and scale models, and builds of 6 ft and more. Sanding, painting and metal plating.',
    price: `A person reviews your file, sketch or photo and sends a written quote. From about $${pricing.printFrom}, one print minimum, no setup fee.`,
    goodFor:
      'Brands, agencies, architects and production work that need one team to own the result: production runs, architectural and scale models, very large builds, painted prints, metal parts and trophies. Next-day printing available.',
    link: ['Architectural models', '/services/architectural-3d-printing/'],
  },
  {
    id: 'makelab',
    name: 'Makelab',
    url: 'https://www.makelab.com/',
    where:
      'Sunset Park, Brooklyn (13 42nd Street). Free pickup; next-day delivery in NYC and northern New Jersey; ships to all 50 states.',
    prints:
      'FDM, SLA, Industrial SLA, MJF and FGF, in plastics only. Parts in one piece up to 94 × 94 × 39 in on FGF.',
    price:
      'Instant online quote; an engineer reviews every file. No minimum order, and bulk discounts start at 10 parts.',
    goodFor:
      'Production plastic parts and prototypes for businesses. FDM lead times start from 1 business day.',
  },
  {
    id: 'b3d-labs',
    name: 'B3D Labs',
    url: 'https://www.b3dlabs.com/',
    where:
      'Industry City, Brooklyn (68 34th Street). Local pickup by appointment; shipping quoted at order.',
    prints:
      'Large-scale FDM and SLA. Also 3D scanning, 3D design, post-processing (sanding, priming, painting, assembly) and metal casting/plating.',
    price:
      'Contact form, with a reply in one to three business days. “No robots, no automated quotes.”',
    goodFor:
      'Window displays, architectural models, props and event pieces. Small prints in 1–3 business days.',
  },
  {
    id: 'emulatics',
    name: 'Emulatics',
    url: 'https://www.emulatics.com/',
    where:
      'Brooklyn (544 Park Avenue). Local shipping across the tri-state area.',
    prints:
      'FDM, SLA, SLS, MJF, large-format FDM up to 1,000 mm, SLM metal (aluminum, 316L and 17-4 PH stainless) and CNC.',
    price: 'Upload your files for a quick quote.',
    goodFor:
      'Fast plastic parts and bulk runs. FDM standard 3–5 days, same or next day expedited.',
  },
  {
    id: 'voxel-magic',
    name: 'Voxel Magic',
    url: 'https://voxel-magic.com/',
    where:
      'Lower East Side, Manhattan (28 Orchard Street). Serving the tri-state area since 2016.',
    prints:
      'FDM, MSLA resin, SLS and MJF. Also 3D CAD design, 3D scanning and laser cutting.',
    price:
      'Custom quote for every job, in “Best”, “Budget” or “Basic” options. Free consultation by phone.',
    goodFor:
      'Quick prints to pick up in Manhattan: most projects ready in 24 to 48 hours, per its FAQ.',
  },
]

// Every competitor fact above, with the page it came from.
const sources = [
  {
    shop: 'Makelab',
    pages: [
      'https://www.makelab.com/compare/3d-printing-services-nyc',
      'https://www.makelab.com/',
    ],
  },
  {
    shop: 'B3D Labs',
    pages: [
      'https://www.b3dlabs.com/',
      'https://www.b3dlabs.com/core-services',
      'https://www.b3dlabs.com/faq',
    ],
  },
  {
    shop: 'Emulatics',
    pages: [
      'https://www.emulatics.com/',
      'https://www.emulatics.com/capabilities-materials',
      'https://www.emulatics.com/quote',
    ],
  },
  {
    shop: 'Voxel Magic',
    pages: [
      'https://voxel-magic.com/',
      'https://voxel-magic.com/faqs',
      'https://voxel-magic.com/about-us',
      'https://voxel-magic.com/how-we-quote',
      'https://voxel-magic.com/services/3d-printing-in-nyc',
    ],
  },
]

// "Which shop fits your job?" cards. Each names every shop on the page whose
// own site lists that kind of work.
const jobs = [
  {
    label: '01',
    title: 'Production runs and large orders',
    shops: 'PrintAWorld, Makelab, Emulatics',
    body: 'Our in-house FDM print farm takes large production orders, and a person reviews the job before it runs. Makelab gives an instant online price, has no minimum order and starts bulk discounts at 10 parts. Emulatics lists bulk runs of hundreds or thousands of parts.',
  },
  {
    label: '02',
    title: 'Very large builds and architectural models',
    shops: 'PrintAWorld, B3D Labs',
    body: 'We print builds of 6 ft and more in sections, and architectural and scale models of buildings, sites and interiors for presentations, competitions and displays. B3D Labs lists large-scale builds, architectural models, window displays and props. Makelab prints plastic parts up to 94 in in one piece on FGF.',
  },
  {
    label: '03',
    title: 'Painted and finished prints',
    shops: 'PrintAWorld, B3D Labs',
    body: 'We sand, prime, paint and metal-plate prints so they leave ready to show. B3D Labs lists sanding, priming, painting and assembly.',
  },
  {
    label: '04',
    title: 'Metal 3D printing and metal plating',
    shops: 'PrintAWorld, Emulatics',
    body: 'We print solid stainless steel with SLM and plate resin prints in metal. Emulatics lists SLM in aluminum and stainless steel. Makelab prints plastics only and points metal parts to us.',
  },
  {
    label: '05',
    title: 'A part or figure from a sketch or photo',
    shops: 'PrintAWorld, B3D Labs, Voxel Magic',
    body: `All three model from a sketch or reference. Our 3D modeling and 3D scanning start at $${pricing.modelingFrom}, and the same team prints and finishes the piece.`,
  },
  {
    label: '06',
    title: 'A print by tomorrow',
    shops: 'PrintAWorld, Voxel Magic, Emulatics, Makelab',
    body: 'We offer next-day printing, with courier delivery across NYC or next-day shipping. Voxel Magic says most projects are ready in 24 to 48 hours. Emulatics lists same or next day expedited FDM. Makelab FDM starts from 1 business day.',
  },
]

// "When another shop is the better choice." Plain about where we don't fit.
const otherShopFits = [
  [
    'Makelab',
    'You want to upload a file and see a price before you talk to anyone, or you need free pickup and next-day delivery in New Jersey as well as NYC. Makelab’s own comparison says it prints only plastics.',
  ],
  [
    'Emulatics',
    'You need SLS or MJF nylon, aluminum SLM, or CNC alongside your prints, or a fast bulk run shipped around the tri-state area.',
  ],
  [
    'Voxel Magic',
    'You want a plain plastic or resin print you can collect in Manhattan within a day or two, and you’re happy to choose between a “Best”, “Budget” or “Basic” quote.',
  ],
  [
    'B3D Labs',
    'You want a large FDM display piece, a retail window, or printer rental, repair or training from the same studio.',
  ],
  [
    'A marketplace or a hobbyist',
    'You want the cheapest print of a hobby file, or parts to resell. A marketplace like Craftcloud or a hobbyist will cost less than we do.',
  ],
]

const askList = [
  'Do you print in-house, or send the job to a partner?',
  'Which process and material do you suggest for how the piece will be used?',
  'What is the largest single piece you can print, and how do you join sections?',
  'Is sanding, priming, painting or plating included, or quoted separately?',
  'Is there a minimum order or a setup fee?',
  'Does the schedule start when I approve the file, or when I pay?',
  'Can I pick it up, and who pays for shipping?',
  'Can you build the file from a sketch, photo or scan, and do I get the file?',
]

const ourFacts = [
  [
    'Price',
    `From about $${pricing.printFrom}, one print minimum, no setup fee`,
  ],
  ['Studios', studios.join('; ')],
  ['Production', 'In-house in our two New York studios'],
  [
    'Processes',
    'FDM print farm, SLA resin, large-format and stainless steel (SLM) metal',
  ],
  ['Orders', 'One print to large production orders'],
  ['Largest builds', '6 ft and more, printed in sections and assembled'],
  ['Speed', 'Next-day printing available, with NYC courier delivery'],
  ['Finishing', 'Sanding, priming, painting and metal plating, quoted per job'],
  ['Design', `3D modeling and 3D scanning from $${pricing.modelingFrom}`],
  [
    'Handoff',
    'Pickup by appointment; ships to all 50 states, next-day shipping available',
  ],
  [
    'Best for',
    'Businesses, brands, agencies, architects, creative and production work',
  ],
  [
    'Look elsewhere for',
    'Cheap hobby prints or parts to resell: a marketplace or hobbyist costs less',
  ],
]

const faqs = [
  {
    question: `What are the best 3D print shops in NYC in ${year}?`,
    answer:
      'It depends on the job. For brands, agencies, architects and production work (large production runs, architectural and scale models, very large builds, painted prints, stainless steel metal, trophies, or a model built from a sketch), choose PrintAWorld in Queens and Brooklyn. For production plastic parts with an instant online price, choose Makelab in Sunset Park. B3D Labs in Industry City suits large display pieces, Emulatics in Brooklyn suits fast and bulk runs in many processes, and Voxel Magic on the Lower East Side suits quick prints to pick up in Manhattan.',
  },
  {
    question: 'Where can I get something 3D printed in NYC?',
    answer: `New York has several 3D print shops. Makelab (Sunset Park, Brooklyn) gives instant online quotes for plastic parts. B3D Labs (Industry City, Brooklyn), Emulatics (Brooklyn) and Voxel Magic (Lower East Side, Manhattan) take projects by quote. PrintAWorld prints in-house at studios in Glendale, Queens and Brooklyn, from about $${pricing.printFrom} with a one-print minimum. Many public libraries and makerspaces also have printers you can use yourself.`,
  },
  {
    question: 'Who prints large 3D printed parts and props in NYC?',
    answer:
      'PrintAWorld prints builds of 6 ft and more in sections, then joins, sands and paints them, and its in-house FDM print farm takes large production orders. B3D Labs lists large-scale builds, window displays and props. Makelab prints plastic parts up to 94 × 94 × 39 in in one piece on its FGF printers, and Emulatics lists large-format FDM up to 1,000 mm.',
  },
  {
    question: 'Who makes 3D printed architectural models in NYC?',
    answer:
      'PrintAWorld 3D prints architectural and scale models of buildings, sites and interiors for client presentations, competitions and displays, with sectioning, assembly, sanding and painting when the model needs them. B3D Labs also lists architectural models among its services.',
  },
  {
    question: 'Where can I get a 3D print painted or finished in NYC?',
    answer:
      'PrintAWorld sands, primes, paints and metal-plates prints in its New York studios, so the piece leaves ready to show. B3D Labs also lists sanding, priming, painting and assembly. Send a photo of the finish you want; finishing is quoted with the print.',
  },
  {
    question: 'Who does metal 3D printing in NYC?',
    answer:
      'PrintAWorld prints solid stainless steel parts with selective laser melting (SLM), in a 250 × 250 × 300 mm build area. Emulatics lists SLM in aluminum and stainless steel. Makelab’s own comparison says it prints only plastics and points metal parts to PrintAWorld. If you want a metal look on a larger or lighter piece, metal plating adds a metal surface to a resin print.',
  },
  {
    question: 'Who makes custom 3D printed trophies in NYC?',
    answer: `PrintAWorld models, 3D prints, paints or plates custom trophies from your idea, logo or sketch, from $${pricing.trophyFrom} per piece with no minimum order. B3D Labs also lists custom trophies among its services.`,
  },
  {
    question: 'Can I get something 3D printed the same day in NYC?',
    answer:
      'Sometimes, for small plastic prints. Emulatics lists same or next day expedited FDM, Voxel Magic says most projects are ready in 24 to 48 hours, and Makelab FDM starts from 1 business day. PrintAWorld offers next-day printing, with courier delivery across NYC; same-day is exceptional, not standard, and painted or very large pieces take longer. Tell us your date and we’ll say what can be done.',
  },
  {
    question: 'Can I pick up a 3D print in NYC, or do shops ship?',
    answer:
      'Both. PrintAWorld offers pickup by appointment at its Glendale and Brooklyn studios, courier delivery across NYC, and shipping to all 50 states with next-day shipping available. Makelab offers free pickup in Sunset Park and next-day delivery in NYC and northern New Jersey. B3D Labs offers pickup by appointment, and Voxel Magic offers local pickup or delivery.',
  },
  {
    question: 'Can a NYC 3D print shop work from a sketch or photo?',
    answer: `Yes. PrintAWorld, B3D Labs and Voxel Magic all build 3D models from a sketch or reference. At PrintAWorld, 3D modeling and 3D scanning start at $${pricing.modelingFrom}, and the same team prints and finishes the piece.`,
  },
  {
    question: 'How much does 3D printing cost in NYC?',
    answer: `It depends on size, material, quantity and finish. PrintAWorld prints start from about $${pricing.printFrom} with no setup fee. Voxel Magic’s FAQ says a small ring costs about $60. Makelab shows an instant price online.`,
  },
  {
    question: 'Who is PrintAWorld a good fit for?',
    answer:
      'Businesses, brands, agencies, architects, and creative and production teams that need one team to own the result, from one print to a large production order, including architectural and scale models for presentations, competitions and displays. It is not the cheapest option for hobby prints or for parts you plan to resell: a marketplace like Craftcloud or a hobbyist will cost less.',
  },
  {
    question: 'How did you pick the shops on this page?',
    answer:
      'We chose shops with a studio in New York City that print for the public and whose websites are current. Makelab’s comparison names Makelab, B3D Labs, Emulatics and PrintAWorld; we added Voxel Magic, which also comes up for NYC 3D printing searches. New Jersey shops, closed shops and online marketplaces without a NYC studio are left out. “Best” here means best for a kind of job: no shop is best at everything. Nobody paid to be listed.',
  },
  {
    question: 'How do I correct a fact on this page?',
    answer: `Email ${nycPrintingCompared.correctionsEmail} with the fact and a link to the page on your website that shows it. We’ll recheck it and update the page and its date.`,
  },
]

// Page-specific lines for PrintShopsGuide.js.
const copy = {
  crumb: 'Best 3D print shops in NYC',
  eyebrow: 'NYC 3D print shops',
  noun: ['shop', 'shops'],
  questionsLink: 'NYC 3D print shop questions',
  tableLabel: 'NYC 3D print shops compared',
  ourPath: '/3d-printing-nyc/',
  about: '3D printing services in New York City',
  h2: {
    compared: 'NYC 3D print shops side by side, from their own websites',
    fit: 'Which NYC 3D print shop fits your job?',
    band: 'What PrintAWorld does as a NYC 3D print shop',
    versus: 'Makelab or PrintAWorld: which NYC 3D print shop?',
    others: 'When another NYC 3D print shop is the better choice',
    ask: 'What to ask a NYC 3D print shop',
    questions: 'Questions about 3D print shops in NYC',
    sources: 'Sources for this list of NYC 3D print shops',
  },
  pickedTitle: 'How we picked the best 3D print shops in NYC',
  picked:
    'Every shop here has a studio in New York City, prints for the public and has a current website. Makelab’s own comparison page lists Makelab, B3D Labs, Emulatics and PrintAWorld. We added Voxel Magic, which also comes up when people search for 3D printing in NYC. New Jersey shops, shops that have closed, online marketplaces without a NYC studio, and libraries or makerspaces where you print yourself are left out. “Best” here means best for a kind of job: no shop is best at everything.',
  band: [
    'We are a production studio for businesses, brands, agencies and creative teams. Everything is made in-house in our two New York studios: an FDM print farm for large production orders, SLA resin for detail and architectural models, large-format printers for builds of 6 ft and more, and stainless steel SLM for metal. The same team sands, paints and plates, and a person reviews every file and owns the result. Next-day printing is available, with courier delivery across NYC or next-day shipping to all 50 states.',
    'We don’t give instant online prices, and we aren’t the cheapest option for hobby prints or parts to resell: a marketplace like Craftcloud or a hobbyist will cost less. If you’d rather upload a file and order without talking to anyone, Makelab fits better.',
  ],
  serviceLink: ['Our 3D printing service', '/services/3d-printing/'],
  askLinkLabel: '3D printing service',
  versus: {
    id: 'makelab',
    paragraphs: [
      'Both print on their own floors in New York (Makelab says “most parts”), both take one print or a large order, and both offer next-day delivery in NYC. The difference is how you buy. Choose Makelab when you want an instant price: it runs FDM, SLA, Industrial SLA, MJF and FGF in Sunset Park, shows a price when you upload a file, has no minimum order and delivers next day across NYC and northern New Jersey.',
      `Choose PrintAWorld when you want a team to own the result: a production run from our FDM print farm, a build bigger than one printer, a piece that needs sanding, paint or plating, a solid stainless steel part, or a model we design from your sketch first. There’s no setup fee, prints start from about $${pricing.printFrom}, and we ship to all 50 states. Makelab’s own page says it prints only plastics and points metal parts to PrintAWorld.`,
    ],
    link: ['Our NYC 3D printing studios', '/3d-printing-nyc/'],
  },
  related: [
    ['3D printing in NYC', '/3d-printing-nyc/'],
    ['3D printing service', '/services/3d-printing/'],
    ['Large-format 3D printing', '/large-scale-3d-printing-service/'],
    ['Custom 3D printing', '/services/custom-3d-printing/'],
    ['Metal 3D printing', '/services/metal-3d-printing/'],
    ['SLA 3D printing', '/services/sla-3d-printing/'],
    ['3D modeling', '/services/3d-modeling/'],
    ['3D scanning in NYC', '/3d-scanning-nyc/'],
  ],
}

const photos = {
  hero: {
    url: 'https://images.ctfassets.net/dovsnf6qrcem/2qVhcDIOuVkHgiLUjtCg3a/0f7ca7f3da72305256fc083aac3af12c/LAL_6513.jpg',
    width: 3000,
    height: 1974,
    alt: 'Two 3D printed guitar-player figures, one raw white print and one chrome plated',
    caption: 'The same print straight off the printer and after chrome plating',
  },
}

module.exports = {
  nycPrintingCompared,
  guide: nycPrintingCompared,
  copy,
  metaTitle,
  description,
  answer,
  pricing,
  shops,
  sources,
  jobs,
  otherShopFits,
  askList,
  ourFacts,
  faqs,
  photos,
}
